/**
 * SCR-010 — Classes list (admin + manager).
 * Admin: branch filter visible + full CRUD.
 * Manager: branch filter hidden; data scoped server-side.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MoreHorizontal, Pencil, Plus, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { ClassForm, type ClassFormValues } from '@client/components/forms/class-form';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import {
  type ClassListItem,
  type ClassStatus,
  classKeys,
  useClasses,
  useCreateClass,
} from '@client/lib/queries/use-classes';
import { useCourses } from '@client/lib/queries/use-courses';
import { useRooms } from '@client/lib/queries/use-rooms';
import { useBranchFilter } from '@client/store/branch-filter';
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/admin/classes/')({
  component: ClassesListPage,
});

const STATUS_META: Record<ClassStatus, { label: string; variant: 'warning' | 'info' | 'success' | 'secondary' | 'destructive' }> = {
  planned:   { label: 'Chờ khai giảng', variant: 'warning' },
  open:      { label: 'Mở ghi danh',    variant: 'info' },
  running:   { label: 'Đang học',        variant: 'success' },
  finished:  { label: 'Đã kết thúc',    variant: 'secondary' },
  cancelled: { label: 'Đã hủy',         variant: 'destructive' },
};

function ClassesListPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';
  const qc = useQueryClient();
  const { selectedBranchId } = useBranchFilter();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClass, setEditClass] = useState<ClassListItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveBranch =
    role === 'manager'
      ? undefined
      : branchFilter !== 'all'
        ? branchFilter
        : selectedBranchId ?? undefined;

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    branchId: effectiveBranch,
    status: statusFilter !== 'all' ? (statusFilter as ClassStatus) : undefined,
  };

  const { data, isLoading, isError, refetch } = useClasses(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const branches = branchesData?.items ?? [];
  const { data: coursesData } = useCourses({ isActive: true, pageSize: 100 });
  const courses = coursesData?.items ?? [];
  const { data: roomsData } = useRooms({
    branchId: effectiveBranch,
    isActive: true,
    pageSize: 100,
  });
  const rooms = roomsData?.items ?? [];
  const createClass = useCreateClass();

  function openCreate() {
    setEditClass(null);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(cls: ClassListItem) {
    setEditClass(cls);
    setFormError(null);
    setDialogOpen(true);
  }

  async function handleCancelClass(cls: ClassListItem) {
    if (!confirm(`Hủy lớp "${cls.name}"? Hành động này không thể hoàn tác.`)) return;
    try {
      await api.patch(`/api/classes/${cls.id}`, { status: 'cancelled' });
      await qc.invalidateQueries({ queryKey: classKeys.all });
      toast.success('Đã hủy lớp học.');
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const body = err.body as { message?: string; activeEnrollmentCount?: number };
        const count = body?.activeEnrollmentCount;
        toast.error(
          count
            ? `Không thể hủy lớp — còn ${count} học viên đang ghi danh.`
            : (body?.message ?? 'Không thể hủy lớp.'),
        );
      } else {
        toast.error('Không thể hủy lớp, thử lại sau.');
      }
    }
  }

  async function handleSubmit(values: ClassFormValues) {
    setFormError(null);
    setIsSubmitting(true);
    try {
      const payload = {
        ...values,
        roomId: values.roomId || undefined,
        endDate: values.endDate || undefined,
        note: values.note || undefined,
        branchId: values.branchId || undefined,
      };
      if (editClass) {
        await api.patch(`/api/classes/${editClass.id}`, payload);
        await qc.invalidateQueries({ queryKey: classKeys.all });
        toast.success('Đã cập nhật lớp học.');
      } else {
        await createClass.mutateAsync(payload);
        toast.success('Đã tạo lớp học.');
      }
      setDialogOpen(false);
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setFormError('Mã lớp đã tồn tại.');
      } else if (isApiError(err) && err.status === 422) {
        const body = err.body as { message?: string; activeEnrollmentCount?: number };
        const count = body?.activeEnrollmentCount;
        setFormError(
          count
            ? `Không thể hủy lớp — còn ${count} học viên đang ghi danh.`
            : (body?.message ?? 'Không thể thực hiện thao tác này.'),
        );
      } else {
        setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: ColumnDef<ClassListItem>[] = [
    {
      accessorKey: 'code',
      header: 'Mã lớp',
      cell: ({ row }) => (
        <Link
          to="/admin/classes/$classId"
          params={{ classId: row.original.id }}
          className="font-mono text-sm text-primary hover:underline"
        >
          {row.original.code}
        </Link>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Tên lớp',
      cell: ({ row }) => (
        <Link
          to="/admin/classes/$classId"
          params={{ classId: row.original.id }}
          className="hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'courseName',
      header: 'Khóa học',
      cell: ({ row }) => row.original.courseName ?? '—',
    },
    {
      accessorKey: 'branchName',
      header: 'Cơ sở',
      cell: ({ row }) => row.original.branchName ?? '—',
    },
    {
      accessorKey: 'leadTeacherName',
      header: 'GV phụ trách',
      cell: ({ row }) => {
        const name = row.original.leadTeacherName;
        const code = row.original.leadTeacherCode;
        if (!name) return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm">{name}</span>
            {code && <span className="text-xs text-muted-foreground font-mono">{code}</span>}
          </div>
        );
      },
    },
    {
      id: 'enrollment',
      header: 'Sĩ số',
      cell: ({ row }) => {
        const { currentEnrollment, capacity } = row.original;
        const isFull = currentEnrollment >= capacity;
        return (
          <span className={isFull ? 'text-destructive font-medium' : ''}>
            {currentEnrollment}/{capacity}
          </span>
        );
      },
    },
    {
      accessorKey: 'startDate',
      header: 'Khai giảng',
      cell: ({ row }) =>
        row.original.startDate
          ? format(new Date(row.original.startDate), 'dd/MM/yyyy')
          : '—',
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: ({ row }) => {
        const meta = STATUS_META[row.original.status] ?? STATUS_META['planned'];
        return (
          <Badge variant={meta?.variant} className="text-xs">
            {meta?.label}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link
                to="/admin/classes/$classId"
                params={{ classId: row.original.id }}
              >
                Xem chi tiết
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openEdit(row.original)}>
              <Pencil className="h-4 w-4 mr-2" /> Sửa
            </DropdownMenuItem>
            {row.original.status !== 'cancelled' && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => void handleCancelClass(row.original)}
              >
                <XCircle className="h-4 w-4 mr-2" /> Hủy lớp
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Lớp học</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={openCreate}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Tạo lớp
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có lớp học nào."
        searchPlaceholder="Tìm tên/mã lớp..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={data ? { page, pageSize: PAGE_SIZE, total: data.total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            {role === 'admin' && (
              <Select
                value={branchFilter}
                onValueChange={(v) => { setBranchFilter(v); setPage(1); }}
              >
                <SelectTrigger className="h-10 w-[160px]">
                  <SelectValue placeholder="Cơ sở" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả cơ sở</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
            >
              <SelectTrigger className="h-10 w-[160px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editClass ? 'Sửa lớp học' : 'Tạo lớp học'}</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <ClassForm
            isEdit={!!editClass}
            isSubmitting={isSubmitting}
            branches={role === 'admin' ? branches : undefined}
            courses={courses.map((c) => ({
              id: c.id,
              code: c.code,
              name: c.name,
              level: c.level,
            }))}
            rooms={rooms.map((r) => ({ id: r.id, name: r.name }))}
            defaultValues={
              editClass
                ? {
                    code: editClass.code,
                    name: editClass.name,
                    courseId: editClass.courseId,
                    branchId: editClass.branchId,
                    roomId: editClass.roomId ?? '',
                    status: editClass.status,
                    startDate: editClass.startDate ?? '',
                    endDate: editClass.endDate ?? '',
                    capacity: editClass.capacity,
                    note: editClass.note ?? '',
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
